import React, { useState, useEffect } from "react";
import axios from "axios";
import SearchResult from "../searchResult/SearchResult";
import styles from "./Search.module.css";
import { apiUrl } from "../../constants";
import FunctionalButton from "../button/FunctionalButton";
import SelectionToggle from "../selectionToggle/SelectionToggle";

function Search({ showNotActiveToggle }) {
  const [result, setResult] = useState([]);
  const [sortedResult, setSortedResult] = useState([]);
  const [filterResult, setFilterResult] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [noResultMessage, toggleNoResultMessage] = useState(false);
  const [country, setCountry] = useState("Netherlands");
  const [active, setActive] = useState("active");
  const [publisherResults, setPublisherResults] = useState([]);
  const [standardResults, setStandardResults] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);

  //Active options for the active toggle
  const activeOptions = ["Active", "Not active"];

  //Country options for the country toggle are fetched from the database and sorted
  useEffect(() => {
    async function getCountryData() {
      const jwtToken = localStorage.getItem("token");
      try {
        const { data } = await axios.get(`${apiUrl}/formsettings/countries`, {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        });

        const countryOptions = [];

        data.map((item) => {
          countryOptions.push(item.country);
        });

        setCountryOptions(countryOptions.sort());
      } catch (e) {
        console.error(e);
      }
    }
    getCountryData();
  }, []);

  // Here we get the active data when loading the page and update it when the country is changed.
  useEffect(() => {
    getInitialData();

    if (country) {
      getInitialData();
      setSearchInput("");
      toggleNoResultMessage(false);
    }
  }, [country]);

  // Here we get the active data when loading the page and update it when the active drop down is changed.
  useEffect(() => {
    getInitialData();

    if (country) {
      getInitialData();
      setSearchInput("");
      toggleNoResultMessage(false);
    }
  }, [active]);

  // Here we get the data form the database
  async function getInitialData() {
    const jwtToken = localStorage.getItem("token");
    try {
      const { data } = await axios.get(
        `${apiUrl}/specsheets/initaldata/${country}/${active}`,
        {
          headers: {
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      setResult(data);
    } catch (e) {
      console.error(e);
    }
  }

  // Here we activate the sortResult function when result is updated.
  useEffect(() => {
    sortResult(result);
  }, [result]);

  // Here we activate the sortResult function when filterResult is updated.
  useEffect(() => {
    sortResult(filterResult);
  }, [filterResult]);

  // Here we sort and split the results before displaying on the page.
  function sortResult(resultToBeSorted) {
    const sortedResult = resultToBeSorted.sort((a, b) => {
      if (a.specsheet.templateName < b.specsheet.templateName) {
        return -1;
      }
      if (a.specsheet.templateName > b.specsheet.templateName) {
        return 1;
      }
      return 0;
    });
    setSortedResult(sortedResult);

    splitSpecsheetsPublisher(sortedResult);
    splitSpecsheetsStandard(sortedResult);
  }

  // create new array with all the publisher specsheets for displaying
  function splitSpecsheetsPublisher(sortedResult) {
    const publisherSpecsheets = sortedResult.filter(
      (item) =>
        item.specsheet.templateName.split("-")[0].trim().toLowerCase() !==
        "standard"
    );

    setPublisherResults(publisherSpecsheets);
  }

  // create new array with all the standard specsheets for displaying
  function splitSpecsheetsStandard(sortedResult) {
    const standardSpecsheets = sortedResult.filter(
      (item) =>
        item.specsheet.templateName.split("-")[0].trim().toLowerCase() ===
        "standard"
    );

    sortBySubheader(standardSpecsheets);
  }

  // Here we sort by subheader if it is a standard specsheet
  function sortBySubheader(standardSpecsheets) {
    const subheadersArray = [];
    standardSpecsheets.map((item, index) => {
      if (index === 0) {
        subheadersArray.push(item.specsheet.listSubheader);
      } else if (!subheadersArray.includes(item.specsheet.listSubheader)) {
        subheadersArray.push(item.specsheet.listSubheader);
      }
    });

    const sortedArray = subheadersArray.sort(function (a, b) {
      a = a.toLowerCase();
      b = b.toLowerCase();
      if (a == b) return 0;
      return a < b ? -1 : 1;
    });

    const resultSortedArray = [];
    sortedArray.map((subheader) => {
      standardSpecsheets.map((item) => {
        if (item.specsheet.listSubheader === subheader) {
          resultSortedArray.push(item);
        }
      });
    });

    setStandardResults(resultSortedArray);
  }

  // After searching we can get the original list of specsheets again
  function clearSearch() {
    getInitialData();
    setSearchInput("");
    toggleNoResultMessage(false);
  }

  // Here we filter the list with results on every keypress
  function filterResultsList(value) {
    toggleNoResultMessage(false);
    const newResult = [];
    const idArray = [];
    // Here we check the specsheetname
    result?.map((specsheetItem) => {
      if (newResult.length === 0) {
        if (
          specsheetItem.specsheet.templateName.toLowerCase().includes(value)
        ) {
          newResult.push(specsheetItem);
          idArray.push(specsheetItem._id);
        }
      } else if (
        !idArray.includes(specsheetItem._id) &&
        specsheetItem.specsheet.templateName.toLowerCase().includes(value)
      ) {
        newResult.push(specsheetItem);
        idArray.push(specsheetItem._id);
      }

      // Here we check the keywords
      specsheetItem.specsheet.keywords.map((keyword) => {
        if (keyword.value.toLowerCase().includes(value)) {
          if (newResult.length === 0) {
            newResult.push(specsheetItem);
            idArray.push(specsheetItem._id);
          } else if (!idArray.includes(specsheetItem._id)) {
            newResult.push(specsheetItem);
            idArray.push(specsheetItem._id);
          }
        }
      });
    });
    if (newResult.length === 0) {
      toggleNoResultMessage(true);
    }
    setFilterResult(newResult);
  }

  // Here we check if we should display the subheader
  function publisherSubheader(templateName, index) {
    if (index === 0) {
      return (
        <h4 className={styles["publisher-subheader"]}>
          {templateName.split("-")[0]}
        </h4>
      );
    } else {
      if (
        templateName.split("-")[0] !==
        publisherResults[index - 1].specsheet.templateName.split("-")[0]
      ) {
        return (
          <h4 className={styles["publisher-subheader"]}>
            {templateName.split("-")[0]}
          </h4>
        );
      }
    }
  }

  // Here we check if we should display the subheader
  function StandardSubheader(listSubheader, index) {
    if (index === 0) {
      return <h4 className={styles["publisher-subheader"]}>{listSubheader}</h4>;
    } else {
      if (
        listSubheader !== standardResults[index - 1].specsheet.listSubheader
      ) {
        return (
          <h4 className={styles["publisher-subheader"]}>{listSubheader}</h4>
        );
      }
    }
  }

  return (
    <div className={styles["container-search"]}>
      <div className={styles["container-settings"]}>
        {countryOptions && (
          <SelectionToggle
            set={setCountry}
            selected={country}
            setSelected={setCountry}
            options={countryOptions}
          />
        )}
        {showNotActiveToggle && (
          <SelectionToggle
            set={setActive}
            selected={active}
            setSelected={setActive}
            options={activeOptions}
          />
        )}
      </div>
      <div className={styles.container}>
        <div className={styles["search-container"]}>
          <div className={styles["search-form-container"]}>
            <div className={styles["search-form"]}>
              <p>Search for a specsheet</p>
              <div className={styles["inputfield-wrapper"]}>
                <input
                  value={searchInput}
                  className={styles["search-input-field"]}
                  placeholder="keyword..."
                  onChange={(e) => {
                    setSearchInput(e.target.value.toLowerCase());
                    filterResultsList(e.target.value.toLowerCase());
                  }}
                />
              </div>
            </div>
            {noResultMessage && (
              <span className={styles.error}>No results please try again.</span>
            )}
            <div className={styles["clear-button-wrapper"]}>
              <FunctionalButton type="button" handleClick={clearSearch}>
                Clear Search
              </FunctionalButton>
            </div>
          </div>
        </div>

        <>
          <div className={styles["result-container"]}>
            <h2>Publisher</h2>

            {publisherResults?.map((sheet, index) => {
              const {
                _id,
                specsheet: { templateName, active },
              } = sheet;

              return (
                <div key={_id}>
                  {publisherSubheader(templateName, index)}
                  <SearchResult
                    key={_id}
                    titleTemplate={templateName}
                    id={_id}
                    active={active}
                  />
                </div>
              );
            })}

            <br />

            <h2>Standard</h2>

            {standardResults?.map((sheet, index) => {
              const {
                _id,
                specsheet: { templateName, active, listSubheader },
              } = sheet;

              return (
                <div key={_id}>
                  {StandardSubheader(listSubheader, index)}
                  <SearchResult
                    key={_id}
                    titleTemplate={templateName}
                    id={_id}
                    active={active}
                  />
                </div>
              );
            })}
          </div>
        </>
      </div>
    </div>
  );
}

export default Search;
